/**
 * Form videos for exercises.
 *
 * Every exercise gets a working link immediately, via a YouTube search built from its
 * name. On top of that you can PIN a specific video you have watched and approved,
 * stored as `video:<slug>` in kv like any other setting.
 *
 * Why search links rather than 41 hardcoded video ids:
 *
 *  - I cannot watch a video, so hardcoding one would be vouching for form guidance I
 *    have not seen, for someone whose plan exists because of a lower-back history.
 *  - For several exercises here a typical top result actively contradicts the plan.
 *    "Leg press, depth stop" says "stop roughly 10 degrees short of where your pelvis
 *    starts to tuck under, do not chase depth"; most leg press videos demonstrate full
 *    depth with the pelvis tucking, which is the loaded lumbar flexion section 3
 *    forbids. Same for the trap-bar, which this plan ramps in at 60% in weeks 9 and 10.
 *  - A pinned id can be deleted or made private mid-block. A search never 404s.
 *
 * So: search by default, pin what you trust, and the coaching note stays next to the
 * link where it can contradict the video.
 */
import type { Exercise, PlanState } from './types.ts';
import { slugify } from './shopping.ts';

export const videoKey = (name: string): string => `video:${slugify(name)}`;

/**
 * Which items are worth a technique video, decided from the data rather than a guess:
 * every lifting exercise carries muscle tags and no recovery item does, and the three
 * admin items ("Hit 9,000 steps", "Weigh in and log it", "Shop and prep for the week")
 * are exactly the ones with an empty rep string. Walks need no demonstration.
 */
export function offersVideo(ex: Exercise): boolean {
  if (ex.m && ex.m.length > 0) return true;
  if (!ex.r || ex.r.trim() === '') return false;
  return !/\bwalks?\b/i.test(ex.n);
}

export function searchUrl(name: string): string {
  return 'https://www.youtube.com/results?search_query='
    + encodeURIComponent(`${name} proper form technique`);
}

/**
 * A pinned URL is user input rendered as an href, so the scheme and host are checked.
 * Anything else is ignored and the search link is used instead — never rendered.
 */
export function isSafeVideoUrl(raw: unknown): raw is string {
  if (typeof raw !== 'string' || raw.trim() === '') return false;
  let u: URL;
  try { u = new URL(raw.trim()); } catch { return false; }
  if (u.protocol !== 'https:') return false;
  const host = u.hostname.toLowerCase().replace(/^www\./, '');
  return host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtu.be';
}

export interface VideoLink {
  url: string;
  pinned: boolean;
}

export function videoFor(state: PlanState, ex: Exercise): VideoLink | null {
  if (!offersVideo(ex)) return null;
  const stored = state.get<unknown>(videoKey(ex.n));
  if (isSafeVideoUrl(stored)) return { url: stored, pinned: true };
  return { url: searchUrl(ex.n), pinned: false };
}
